import { fromCents } from '../../moneyUtils'

// General-purpose, read-only deserializer for a RatingNode + its linked
// RatingTable(s) — unlike benefitRatingCodec's decompileTarget (which only
// succeeds for the exact shape AddBenefitWizard itself generates), this
// reads ANY combination: any table_type, any dimensions, any segment
// expression the backend's bounded grammar allows (comparisons joined by
// and/or — see product-configuration's RatingSegmentDto), any number of
// chained tables, and a formula/value-ref/flat node base. It never fails —
// worst case, a field is shown as "unknown"/raw rather than a decompile
// error. Display only: this never drives what gets saved.
// docs/2026-09-04-benefit-pricing-wizard-design.md §9 (read-only summary,
// added 2026-09-04, in place of a fully generic editable rule builder).

export const TABLE_TYPE_LABEL = {
  base_rate: 'Base Rate — replaces the base',
  adj_factor: 'Adjustment Factor — multiplies the running value',
  adj_amount: 'Adjustment Amount — adds to the running value',
  adj_factor_quantity: 'Adjustment Factor (Quantity)',
  cap: 'Cap — limits the running value'
}

const NO_MATCH_ACTION_LABEL = {
  reject: 'no match rejects the quote',
  use_default: 'no match uses the table default',
  leave_unchanged: 'no match leaves the running value unchanged',
  refer: 'no match refers the quote for manual review'
}

// `valueKind`: 'money' (cover/premium — value_in_cents is currency) or
// 'number' (waiting_period — value_in_cents is a raw cardinality).
const describeCellValue = (cell, valueKind) => {
  if (cell.value_in_cents !== undefined && cell.value_in_cents !== null) {
    const suffix = cell.granularity
      ? ` ${cell.granularity}${cell.value_in_cents === 1 ? '' : 's'}`
      : ''
    return valueKind === 'money'
      ? fromCents(cell.value_in_cents) || '0'
      : `${cell.value_in_cents}${suffix}`
  }
  if (cell.value_ref) return `fact: ${cell.value_ref}`
  if (cell.value_formula) return `formula: ${cell.value_formula}`
  return '(no value set)'
}

const describeCellCondition = (table, cell) => {
  const labels = cell.segment_refs || []
  if (labels.length === 0) return '(no condition)'
  const expressions = labels.map(label => {
    const segment = (table.segments || []).find(s => s.label === label)
    return segment ? segment.expression : `[missing segment "${label}"]`
  })
  return expressions.join(' AND ')
}

// Describes one linked table as a list of {condition, value} rows, in the
// table's own cell order (first-match-wins, per RatingTable semantics).
export const describeTable = (table, valueKind) => {
  if (!table) return null
  const rows = (table.cells || []).map(cell => ({
    condition: describeCellCondition(table, cell),
    value: describeCellValue(cell, valueKind)
  }))
  return {
    guid: table.guid,
    name: table.name || table.code || table.guid,
    tableType: table.table_type,
    tableTypeLabel: TABLE_TYPE_LABEL[table.table_type] || table.table_type,
    dimensions: table.dimensions || [],
    rows,
    noMatchLabel:
      NO_MATCH_ACTION_LABEL[table.no_match_action] ||
      'no match behavior uses the table-type default'
  }
}

const describeBase = (node, valueKind) => {
  if (valueKind === 'waiting_period') {
    const base = node.base_waiting_period
    if (!base) return 'No base waiting period set on this node'
    return `${base.cardinality} ${base.granularity}${
      base.cardinality === 1 ? '' : 's'
    }`
  }
  if (
    node.base_amount_in_cents !== undefined &&
    node.base_amount_in_cents !== null
  ) {
    return fromCents(node.base_amount_in_cents) || '0'
  }
  if (node.base_value_ref) return `fact: ${node.base_value_ref}`
  if (node.base_formula) return `formula: ${node.base_formula}`
  return 'No base value set on this node'
}

// Full description of a node + its chained tables, in order. `valueKind`:
// 'money' | 'waiting_period' (waiting-period nodes describeBase differently
// and their table cells are raw cardinalities, not currency).
export const describeRatingNode = (node, ratingTables, valueKind) => {
  if (!node) return null
  const tableRefs = (node.tables || [])
    .slice()
    .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
  const tables = tableRefs
    .map(ref =>
      (ratingTables || []).find(t => t.guid === ref.rating_table_guid)
    )
    .map(table =>
      table
        ? describeTable(
            table,
            valueKind === 'waiting_period' ? 'number' : 'money'
          )
        : null
    )
  return {
    nodeType: node.node_type,
    appliesTo: node.applies_to,
    base: describeBase(node, valueKind),
    tables
  }
}
